import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { ApiError } from '@/shared/api/errors';
import { AiNotice } from '@/shared/ui/AiNotice';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { RefreshIcon, SparkIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';

import { AI_DRAFT_MODES, AI_DRAFT_PATIENCE_MS } from './aiDraftModes';
import type { AiDraftMode, OutgoingKind } from './types';
import { useAiDraft, useAskAiDraft } from './useEmails';

const MAX_INSTRUCTIONS = 2_000;

/** What the form gets back when the person picks the proposal. */
export interface AiDraftChoice {
  body: string;
  subject: string | null;
}

/**
 * «Redactar con IA» (contract v10.56, ADR 0053 of the back), over the mail manager's form.
 *
 * `write` first asks what the message should say and how; the others go straight to the
 * model with what is written. Then the two versions side by side, the person's and the
 * AI's, and the person picks one, as Gmail does. Nothing is sent from here: the version
 * picked goes into the form, which the form can undo, and leaves with «Enviar» like
 * anything else written there.
 *
 * Only the part above the signature changes: the back cuts the message there and puts the
 * signature and the quote back under the proposal, so `body` is already the whole message.
 */
export function AiDraftDialog({
  mode,
  kind,
  sourceEmailId,
  mailbox,
  subject,
  body,
  signed = false,
  onUse,
  onClose,
}: {
  mode: AiDraftMode;
  kind: OutgoingKind;
  sourceEmailId: string | null;
  mailbox: string;
  subject: string;
  body: string;
  /** Contract v10.57: a signature goes below, apart from `body`. */
  signed?: boolean;
  onUse: (choice: AiDraftChoice) => void;
  onClose: () => void;
}) {
  const ask = useAskAiDraft();
  const [draftId, setDraftId] = useState<string | null>(null);
  const [instructions, setInstructions] = useState('');
  const [late, setLate] = useState(false);
  const draft = useAiDraft(draftId);
  const asked = useRef(false);
  const id = useId();
  const writing = mode === 'write';
  const row = draft.data ?? (draftId ? ask.data : undefined);
  const waiting = ask.isPending || (row?.status === 'pending' && !draft.isError && !late);

  function request(): void {
    setDraftId(null);
    setLate(false);
    ask.mutate(
      {
        mode,
        kind,
        source_email_id: sourceEmailId,
        ...(kind === 'new' ? { mailbox } : {}),
        instructions: instructions.trim(),
        subject,
        body,
        ...(signed ? { signed: true } : {}),
      },
      { onSuccess: (answer) => setDraftId(answer.id) },
    );
  }

  // A review needs nothing more from the person: it goes as soon as the sheet opens. The
  // ref keeps it to once, also when React mounts the sheet twice in development.
  useEffect(() => {
    if (writing || asked.current) return;
    asked.current = true;
    request();
    // Only on opening: what is written does not change while the sheet is up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draftId) return;
    const timer = window.setTimeout(() => setLate(true), AI_DRAFT_PATIENCE_MS);
    return () => window.clearTimeout(timer);
  }, [draftId]);

  function again(): void {
    // Writing goes back to what was asked, to change it; a review asks again as it was.
    if (writing) {
      setDraftId(null);
      ask.reset();
      return;
    }
    request();
  }

  const done = row?.status === 'done' ? row : null;
  const failed = row?.status === 'failed' ? row.error : null;
  const gone = draft.error instanceof ApiError && draft.error.status === 404;
  // What writing asks for first, and again after «Cambiar lo que pedí»; a request the
  // back refused (422, 429) is said under it, with what was written still there.
  const asking = writing && draftId === null && !ask.isPending;
  const stopped = !asking && (ask.isError || Boolean(failed) || gone || late || draft.isError);

  const footer = done ? (
    <>
      <Button variant="ghost" icon={<RefreshIcon size={14} />} onClick={again}>
        {writing ? 'Cambiar lo que pedí' : 'Otra versión'}
      </Button>
      <Button variant="secondary" onClick={onClose}>
        Quedarme con la mía
      </Button>
      <Button
        variant="ai"
        data-autofocus
        onClick={() => onUse({ body: done.body ?? body, subject: done.subject ?? null })}
      >
        Usar esta versión
      </Button>
    </>
  ) : stopped ? (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cerrar
      </Button>
      <Button variant="ai" icon={<RefreshIcon size={14} />} onClick={again}>
        Volver a intentarlo
      </Button>
    </>
  ) : !asking ? (
    <Button variant="secondary" onClick={onClose}>
      Cancelar
    </Button>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancelar
      </Button>
      <Button
        variant="ai"
        type="submit"
        form={`${id}-ask`}
        icon={<SparkIcon size={14} />}
        disabled={!instructions.trim()}
      >
        Redactar
      </Button>
    </>
  );

  return (
    <Dialog
      title={AI_DRAFT_MODES[mode]}
      description={
        writing
          ? kind === 'new'
            ? 'Explica qué quieres decir y la IA lo escribe. Tú lo revisas antes de enviar.'
            : 'Explica qué quieres contestar y la IA lo escribe teniendo en cuenta la conversación. Tú lo revisas antes de enviar.'
          : 'La IA repasa lo que has escrito encima de la firma. Eliges tú qué versión se queda.'
      }
      size="lg"
      tone="ai"
      onClose={onClose}
      footer={footer}
    >
      {asking ? (
        <form
          id={`${id}-ask`}
          className="flex flex-col gap-1"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (instructions.trim()) request();
          }}
        >
          <label htmlFor={`${id}-instructions`} className="text-ink-2 text-[13.5px] font-medium">
            ¿Qué quieres decir?
          </label>
          <textarea
            id={`${id}-instructions`}
            className="field field-ai h-auto min-h-28 py-2"
            rows={4}
            maxLength={MAX_INSTRUCTIONS}
            value={instructions}
            placeholder="Por ejemplo: que el pedido sale el jueves y que nos diga si necesita factura. Tono cercano."
            onChange={(event) => setInstructions(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                if (instructions.trim()) request();
              }
            }}
          />
          <p className="text-ink-4 text-[12.5px] leading-snug">
            Los precios, fechas o datos que no digas los deja entre corchetes para que los rellenes.
            Lo que ya tengas escrito no se pierde: verás las dos versiones.
          </p>
          {ask.isError ? (
            <ErrorMessage error={ask.error} className="mt-2">
              No se pudo pedir a la IA
            </ErrorMessage>
          ) : null}
        </form>
      ) : waiting ? (
        <Writing label={writing ? 'La IA está escribiendo…' : 'La IA está repasando tu texto…'} />
      ) : ask.isError ? (
        <ErrorMessage error={ask.error}>No se pudo pedir a la IA</ErrorMessage>
      ) : failed ? (
        <Callout tone="warn" title="La IA no ha podido hacerlo">
          {failed}
        </Callout>
      ) : gone || late ? (
        <Callout tone="warn" title="La IA no ha respondido">
          Ha tardado demasiado y la petición ha caducado. Vuelve a intentarlo.
        </Callout>
      ) : draft.isError ? (
        <ErrorMessage error={draft.error}>No se pudo saber qué ha escrito la IA</ErrorMessage>
      ) : done ? (
        <Compare original={done.original ?? ''} proposal={done.proposal ?? ''} />
      ) : null}
      {done?.subject ? (
        <p className="border-line-2 bg-paper-2 text-ink-2 rounded-md border px-3 py-2 text-[13.5px]">
          Asunto propuesto: <strong className="text-ink">{done.subject}</strong>
        </p>
      ) : null}
      {done ? <AiNotice /> : null}
    </Dialog>
  );
}

/**
 * The two versions side by side (one over the other on a phone). With nothing written
 * yet, only the proposal: there is nothing to compare it with.
 */
function Compare({ original, proposal }: { original: string; proposal: string }) {
  const proposalPanel = (
    <Version label="Propuesta de la IA" text={proposal} tone="ai" icon={<SparkIcon size={13} />} />
  );
  if (!original.trim()) return proposalPanel;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Version label="Lo que tenías" text={original} tone="plain" />
      {proposalPanel}
    </div>
  );
}

function Version({
  label,
  text,
  tone,
  icon,
}: {
  label: string;
  text: string;
  tone: 'plain' | 'ai';
  icon?: ReactNode;
}) {
  const ai = tone === 'ai';
  return (
    <section
      aria-label={label}
      className={cn(
        'flex min-w-0 flex-col overflow-hidden rounded-lg border',
        ai ? 'border-ai/30 bg-card shadow-sm' : 'border-line-2 bg-paper-2',
      )}
    >
      <h3
        className={cn(
          'inline-flex items-center gap-1.5 border-b px-3.5 py-2 text-[13px] font-medium',
          ai ? 'border-ai/15 bg-ai-soft text-ai-2' : 'border-line-2 text-ink-3',
        )}
      >
        {icon}
        {label}
      </h3>
      <div
        className={cn(
          'max-h-80 flex-1 overflow-y-auto px-3.5 py-3 text-[14px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]',
          ai ? 'text-ink' : 'text-ink-2',
        )}
      >
        {text}
      </div>
    </section>
  );
}

/**
 * While the model writes: lines that fill in, in the AI's blue, where the proposal will
 * be. It says what is coming better than a wheel, and it is still a `status` with its
 * words for whoever does not see it. Still for whoever asked for less motion.
 */
function Writing({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="border-ai/20 bg-ai-soft/40 flex flex-col gap-3.5 rounded-lg border px-4 py-5"
    >
      <span className="text-ai inline-flex items-center gap-2 text-[14px] font-medium">
        <SparkIcon size={16} className="motion-safe:animate-pulse" />
        {label}
      </span>
      <span aria-hidden="true" className="flex flex-col gap-2.5">
        {WRITING_LINES.map((width, index) => (
          <span
            key={width}
            className={cn('bg-ai/15 h-2.5 rounded-full motion-safe:animate-pulse', width)}
            style={{ animationDelay: `${index * 150}ms` }}
          />
        ))}
      </span>
    </div>
  );
}

const WRITING_LINES = ['w-11/12', 'w-full', 'w-9/12', 'w-5/12'];
